import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Grid,
  Typography,
  Paper,
  Box,
  Alert
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import envVariable from '../EnvironmentVariables.json';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const WeeklyScheduleModal = ({ open, onClose, mechanic, scId, isAddMode = false, onScheduleSave }) => {
  const [scheduleByDay, setScheduleByDay] = useState({
    0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load existing schedule when modal opens
  useEffect(() => {
    if (open && mechanic) {
      // Skip fetching if in add mode (no user_id yet)
      if (!isAddMode) {
        fetchSchedule();
      } else {
        // Reset to empty schedule in add mode
        setScheduleByDay({
          0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: []
        });
      }
    }
  }, [open, mechanic, isAddMode]);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      setError('');
      const apiKey = localStorage.getItem('api_key') || localStorage.getItem('@api_key') || '';
      const mechId = mechanic?.user_id ?? mechanic?.id; // support both shapes
      const url = `${envVariable.serverId}/WeeklySchedule/getWeeklySchedule.php?mechanic_id=${mechId}&sc_id=${scId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': apiKey,
        },
      });
      const data = await response.json();

      if (data.type === 'success') {
        setScheduleByDay(data.schedule_by_day);
      } else {
        setError(data.message || 'Failed to load schedule');
      }
    } catch (err) {
      setError('Error loading schedule: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const addTimeBlock = (dayOfWeek) => {
    setScheduleByDay(prev => ({
      ...prev,
      [dayOfWeek]: [
        ...prev[dayOfWeek],
        { start_time: '09:00', end_time: '17:00', schedule_id: null }
      ]
    }));
  };

  const removeTimeBlock = (dayOfWeek, index) => {
    setScheduleByDay(prev => ({
      ...prev,
      [dayOfWeek]: prev[dayOfWeek].filter((_, i) => i !== index)
    }));
  };

  const updateTimeBlock = (dayOfWeek, index, field, value) => {
    setScheduleByDay(prev => ({
      ...prev,
      [dayOfWeek]: prev[dayOfWeek].map((block, i) =>
        i === index ? { ...block, [field]: value } : block
      )
    }));
  };

  const validateSchedule = () => {
    // Check for overlapping time blocks on the same day
    for (let day = 0; day <= 6; day++) {
      const blocks = scheduleByDay[day];
      for (let i = 0; i < blocks.length; i++) {
        const blockA = blocks[i];
        const startA = blockA.start_time;
        const endA = blockA.end_time;

        // Check start < end
        if (startA >= endA) {
          return `Invalid time range on ${DAYS_OF_WEEK[day].label}: start time must be before end time`;
        }

        // Check for overlaps with other blocks on same day
        for (let j = i + 1; j < blocks.length; j++) {
          const blockB = blocks[j];
          const startB = blockB.start_time;
          const endB = blockB.end_time;

          // Check if blocks overlap
          if (startA < endB && endA > startB) {
            return `Overlapping time blocks on ${DAYS_OF_WEEK[day].label}`;
          }
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    setError('');
    setSuccess('');

    // Validate
    const validationError = validateSchedule();
    if (validationError) {
      setError(validationError);
      return;
    }

    // Convert schedule_by_day to flat array of blocks
    // Normalize time strings to HH:MM:SS (avoid duplicating seconds)
    const normalizeTime = (t) => {
      if (!t) return '';
      const s = String(t).trim();
      const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (!m) return s; // leave unknown formats as-is
      const hh = m[1].padStart(2, '0');
      const mm = m[2];
      const ss = m[3] ?? '00';
      return `${hh}:${mm}:${ss}`;
    };

    const schedule_blocks = [];
    for (let day = 0; day <= 6; day++) {
      scheduleByDay[day].forEach(block => {
        schedule_blocks.push({
          day_of_week: day,
          start_time: normalizeTime(block.start_time),
          end_time: normalizeTime(block.end_time)
        });
      });
    }

    // If in add mode, just pass the schedule back to parent without saving to backend
    if (isAddMode && onScheduleSave) {
      onScheduleSave(schedule_blocks);
      return;
    }

    try {
      setLoading(true);
      const apiKey = localStorage.getItem('api_key') || localStorage.getItem('@api_key') || '';
      const mechId = mechanic?.user_id ?? mechanic?.id;
      const response = await fetch(
        `${envVariable.serverId}/WeeklySchedule/saveWeeklySchedule.php`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': apiKey,
          },
          body: JSON.stringify({
            mechanic_id: mechId,
            sc_id: scId,
            schedule_blocks
          })
        }
      );

      const data = await response.json();

      if (data.type === 'success') {
        setSuccess('Weekly schedule saved successfully!');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError(data.message || 'Failed to save schedule');
      }
    } catch (err) {
      setError('Error saving schedule: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!mechanic) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Weekly Schedule for {mechanic.first_name} {mechanic.last_name}
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Define regular working hours for each day of the week. These will be used
          to automatically generate availability when you click &quot;Generate Week&quot; on the calendar.
        </Typography>

        {DAYS_OF_WEEK.map(day => (
          <Paper key={day.value} elevation={1} sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1" fontWeight="bold">
                {day.label}
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => addTimeBlock(day.value)}
              >
                Add Block
              </Button>
            </Box>

            {scheduleByDay[day.value].length === 0 && (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                No working hours defined
              </Typography>
            )}

            {scheduleByDay[day.value].map((block, index) => (
              <Grid container spacing={2} key={index} alignItems="center" sx={{ mt: 1 }}>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label="Start Time"
                    type="time"
                    value={block.start_time}
                    onChange={(e) => updateTimeBlock(day.value, index, 'start_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={5}>
                  <TextField
                    fullWidth
                    label="End Time"
                    type="time"
                    value={block.end_time}
                    onChange={(e) => updateTimeBlock(day.value, index, 'end_time', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={2}>
                  <IconButton
                    color="error"
                    onClick={() => removeTimeBlock(day.value, index)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Paper>
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Schedule'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeeklyScheduleModal;
